/**
 * Shared game data bridge (#684) — the PARENT side of the `shared/` FS hook.
 *
 * Sibling of `useCloudSave` (#450): the same inline FS-hook script (injected by
 * `PyxelPlayer.vue` / `public/pyxel-frame.html` when `cloudSaveSessionId` is set)
 * reports writes under BOTH tracked prefixes, and each parent bridge picks up only
 * its own prefix — `.pyxel/` for cloud save, `shared/` for this one.
 *
 * What this does:
 * - on start / on the frame's `ready`: GET the session's shared files and push them
 *   into the frame, so the game can read `shared/ranking.json` from the first frame.
 * - when the game writes `shared/score.json`: debounce, PUT it, then push the
 *   freshly aggregated ranking back in (`overwrite: true`, so the game sees it move).
 *
 * Anonymous players do not take part (the API requires auth): we then inject nothing,
 * the game gets FileNotFoundError on `shared/ranking.json` and degrades to solo —
 * the same "no file yet → default" idiom as the first launch of a saved game.
 *
 * Security: the iframe never sees the JWT and makes no network calls; this trusted
 * parent context mediates everything. The scope is resolved SERVER-side from the
 * session, so the game cannot aim its score at another app's leaderboard.
 *
 * Protocol (postMessage, `type: '__camFs'`) — shared with useCloudSave:
 *   iframe → parent  { op: 'ready' }
 *   iframe → parent  { op: 'save', path: 'shared/score.json', bytes: Uint8Array }
 *   parent → iframe  { op: 'load', files: [{ path, bytes, overwrite }] }
 *
 * See doc/architecture/cloud_save.md.
 */

import { useSharedDataApi } from './api/useSharedDataApi'

const SHARED_PREFIX = 'shared/'
// 提出のデバウンス。サーバ側は連投を 429 で弾く（services/shared_data.py）ので、
// ゲームが毎フレーム書いてしまっても 1 リクエストに畳む。
const DEBOUNCE_MS = 1000

interface InjectFile {
  path: string
  bytes: Uint8Array
  overwrite: boolean
}

interface CamFsMessage {
  type?: string
  op?: string
  path?: string
  bytes?: Uint8Array
}

function u8ToB64(bytes: Uint8Array): string {
  let binary = ''
  for (const b of bytes) binary += String.fromCharCode(b)
  return btoa(binary)
}

function b64ToU8(b64: string): Uint8Array {
  const binary = atob(b64)
  const out = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i)
  return out
}

export function useSharedData() {
  const { listShared, putShared } = useSharedDataApi()

  let iframeEl: HTMLIFrameElement | null = null
  // #748: cross-origin フレーム経路では送信 targetOrigin と受信 origin をこの値に絞る。
  let frameOrigin: string | undefined
  let sessionId = ''
  let handler: ((e: MessageEvent) => void) | null = null
  let injectFiles: InjectFile[] = []
  let prefetchDone = false
  // start/stop をまたいだ非同期の取りこぼしを捨てるための世代番号。
  // stop() 中の flush() が返るのは次の start() のあとになりうるので、
  // 世代が変わっていたら「前のセッションのランキング」を注入してはいけない。
  let generation = 0

  const timers = new Map<string, ReturnType<typeof setTimeout>>()
  const latest = new Map<string, Uint8Array>()

  function isAuthed(): boolean {
    return useAuthInfo().isAuthenticated
  }

  function toInjectFiles(files: { filename: string, content: string }[]): InjectFile[] {
    return files.map(f => ({
      path: `${SHARED_PREFIX}${f.filename}`,
      bytes: b64ToU8(f.content),
      // ランキングはプレイ中に更新されるので、既にある版を必ず置き換える。
      overwrite: true,
    }))
  }

  function push(files: InjectFile[]): void {
    const win = iframeEl?.contentWindow
    if (!win || files.length === 0) return
    win.postMessage({ type: '__camFs', op: 'load', files }, frameOrigin ?? '*')
  }

  async function prefetch(): Promise<void> {
    const era = generation
    if (!isAuthed() || !sessionId) {
      // 未認証は共有データに参加しない（ゲームはソロに degrade）。
      prefetchDone = true
      return
    }
    try {
      const res = await listShared(sessionId)
      if (era !== generation) return
      injectFiles = toInjectFiles(res.files ?? [])
    }
    catch (e) {
      console.error('[sharedData] prefetch failed', e)
      if (era !== generation) return
      injectFiles = []
    }
    prefetchDone = true
    push(injectFiles)
  }

  function onMessage(e: MessageEvent): void {
    if (!iframeEl || e.source !== iframeEl.contentWindow) return
    if (frameOrigin && e.origin !== frameOrigin) return // #748 受信 origin 厳格化
    const d = e.data as CamFsMessage
    if (!d || d.type !== '__camFs') return
    if (d.op === 'ready') {
      if (prefetchDone) push(injectFiles)
      return
    }
    if (d.op !== 'save' || typeof d.path !== 'string' || !(d.bytes instanceof Uint8Array)) return
    if (!d.path.startsWith(SHARED_PREFIX)) return // `.pyxel/` は useCloudSave の担当
    scheduleSubmit(d.path.slice(SHARED_PREFIX.length), d.bytes)
  }

  function scheduleSubmit(filename: string, bytes: Uint8Array): void {
    latest.set(filename, bytes)
    const prev = timers.get(filename)
    if (prev) clearTimeout(prev)
    timers.set(filename, setTimeout(() => {
      timers.delete(filename)
      void flush(filename)
    }, DEBOUNCE_MS))
  }

  async function flush(filename: string): Promise<void> {
    const bytes = latest.get(filename)
    if (!bytes) return
    if (!isAuthed() || !sessionId) return
    const era = generation
    try {
      const res = await putShared(sessionId, filename, u8ToB64(bytes))
      if (era !== generation) return // 別のセッションに切り替わっている
      injectFiles = toInjectFiles(res.files ?? [])
      push(injectFiles)
    }
    catch (e) {
      // 提出の失敗はゲームを止めない（順位が更新されないだけ）。
      console.error('[sharedData] submit failed', filename, e)
    }
  }

  function flushAll(): void {
    for (const filename of Array.from(timers.keys())) {
      const t = timers.get(filename)
      if (t) clearTimeout(t)
      timers.delete(filename)
      void flush(filename)
    }
  }

  function start(
    iframe: HTMLIFrameElement,
    opts: { sessionId: string, frameOrigin?: string },
  ): void {
    stop()
    iframeEl = iframe
    sessionId = opts.sessionId
    frameOrigin = opts.frameOrigin
    prefetchDone = false
    injectFiles = []
    handler = onMessage
    window.addEventListener('message', handler)
    void prefetch()
  }

  function stop(): void {
    // **順序が大事**: 先に flushAll() で「最後の提出」を出してから世代を進める。
    // 提出そのものは通したいが（そのスコアは有効）、返ってきたランキングを
    // 次のセッションのフレームへ注入してはいけない。
    flushAll()
    generation += 1
    if (handler) {
      window.removeEventListener('message', handler)
      handler = null
    }
    iframeEl = null
    frameOrigin = undefined
    sessionId = ''
    prefetchDone = false
    injectFiles = []
  }

  onUnmounted(stop)

  return { start, stop }
}
